import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Eye,
  Download,
  CreditCard,
  MoreVertical,
  Plus,
  ChevronLeft,
  ChevronRight,
  Filter,
  Trash2,
  User,
  Receipt,
  Banknote,
  Building2,
  Shield,
  Globe,
  FileText,
} from 'lucide-react'

import { useToast } from '@hooks/use-toast'
import { billingApi } from '@services/billing.service'
import { Invoice } from '@/types'
import { ShiftBillingHeader } from '@components/billing/ShiftBillingHeader'
import { ShiftBillingKPIs } from '@components/billing/ShiftBillingKPIs'
import { CreateInvoiceDialog } from '@components/billing/CreateInvoiceDialog'
import { InvoiceDetailDialog } from '@components/billing/InvoiceDetailDialog'
import { RecordPaymentDialog } from '@components/billing/RecordPaymentDialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@components/ui/dropdown-menu'
import { formatCurrency } from '@/lib/utils'
import { cn } from '@lib/utils'
import { useClinicFormat } from '@context/ClinicTimezoneContext'

// ─── Status pills ─────────────────────────────────────────────────────────────

const statusPillConfig: Record<string, { pill: string; dot: string; label: string }> = {
  paid: {
    pill: 'border border-emerald-600/30 bg-emerald-50/70 text-emerald-700',
    dot: 'bg-emerald-500',
    label: 'Pagado',
  },
  pending: {
    pill: 'border border-amber-500/30 bg-amber-50/70 text-amber-700',
    dot: 'bg-amber-400',
    label: 'Pendiente',
  },
  overdue: {
    pill: 'border border-red-400/30 bg-red-50/70 text-red-600',
    dot: 'bg-red-500',
    label: 'Vencida',
  },
  partially_paid: {
    pill: 'border border-amber-500/30 bg-amber-50/70 text-amber-700',
    dot: 'bg-amber-400',
    label: 'Parcial',
  },
  draft: {
    pill: 'border border-gray-300/50 bg-gray-50 text-gray-500',
    dot: 'bg-gray-400',
    label: 'Borrador',
  },
  sent: {
    pill: 'border border-sky-400/30 bg-sky-50/70 text-sky-600',
    dot: 'bg-sky-400',
    label: 'Enviada',
  },
  cancelled: {
    pill: 'border border-gray-300/40 bg-gray-50 text-gray-400',
    dot: 'bg-gray-300',
    label: 'Cancelada',
  },
}

// ─── Payment method icons ─────────────────────────────────────────────────────

const paymentMethodConfig: Record<string, { icon: React.ReactNode; label: string }> = {
  cash: { icon: <Banknote className="h-3 w-3" />, label: 'Efectivo' },
  credit_card: { icon: <CreditCard className="h-3 w-3" />, label: 'T. Crédito' },
  debit_card: { icon: <CreditCard className="h-3 w-3" />, label: 'T. Débito' },
  check: { icon: <FileText className="h-3 w-3" />, label: 'Cheque' },
  bank_transfer: { icon: <Building2 className="h-3 w-3" />, label: 'Transferencia' },
  insurance: { icon: <Shield className="h-3 w-3" />, label: 'Seguro' },
  online_payment: { icon: <Globe className="h-3 w-3" />, label: 'En línea' },
}

// ─── Tabs ────────────────────────────────────────────────────────────────────

const STATUS_TABS = [
  { value: 'all', label: 'Todas' },
  { value: 'paid', label: 'Pagado' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'overdue', label: 'Vencida' },
  { value: 'partially_paid', label: 'Parcial' },
  { value: 'draft', label: 'Borrador' },
]

const PAGE_SIZE = 15

// ─── Time grouping ────────────────────────────────────────────────────────────

function getMonthKey(dateStr: string): { key: string; label: string } {
  const now = new Date()
  const d = new Date(dateStr || now)
  const nowMonth = now.getFullYear() * 12 + now.getMonth()
  const dMonth = d.getFullYear() * 12 + d.getMonth()
  const diff = nowMonth - dMonth
  const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}`
  let label: string
  if (diff === 0) label = 'Este mes'
  else if (diff === 1) label = 'Mes anterior'
  else label = new Intl.DateTimeFormat('es-MX', { month: 'long', year: 'numeric' }).format(d)
  return { key, label }
}

function groupInvoicesByMonth(invoices: Invoice[]) {
  const ordered: string[] = []
  const map = new Map<string, { label: string; invoices: Invoice[] }>()
  for (const inv of invoices) {
    const { key, label } = getMonthKey(inv.invoice_date || inv.created_at || '')
    if (!map.has(key)) {
      map.set(key, { label, invoices: [] })
      ordered.push(key)
    }
    map.get(key)!.invoices.push(inv)
  }
  return ordered.map((k) => map.get(k)!)
}

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function BillingPage() {
  const { toast } = useToast()
  const navigate = useNavigate()
  const { formatDate } = useClinicFormat()

  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [metricsInvoices, setMetricsInvoices] = useState<Invoice[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [showDateFilter, setShowDateFilter] = useState(false)

  const [createOpen, setCreateOpen] = useState(false)
  const [detailInvoice, setDetailInvoice] = useState<Invoice | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [paymentInvoice, setPaymentInvoice] = useState<Invoice | null>(null)
  const [paymentOpen, setPaymentOpen] = useState(false)

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  useEffect(() => {
    setPage(0)
  }, [statusFilter, dateFrom, dateTo])

  useEffect(() => {
    loadInvoices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, dateFrom, dateTo])

  useEffect(() => {
    loadMetrics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo])

  const buildParams = () => {
    const params: Record<string, any> = {}
    if (statusFilter !== 'all') params.status = statusFilter
    if (dateFrom) params.date_from = dateFrom
    if (dateTo) params.date_to = dateTo
    return params
  }

  const loadInvoices = async () => {
    try {
      setLoading(true)
      const response = await billingApi.getInvoices({
        ...buildParams(),
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      })
      setInvoices(response.items)
      setTotal(response.total)
    } catch {
      toast({ title: 'Error', description: 'Error al cargar facturas', variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  const loadMetrics = async () => {
    try {
      const params: Record<string, any> = { limit: 500 }
      if (dateFrom) params.date_from = dateFrom
      if (dateTo) params.date_to = dateTo
      const response = await billingApi.getInvoices(params)
      setMetricsInvoices(response.items)
    } catch { /* best-effort */ }
  }

  const reload = () => {
    loadInvoices()
    loadMetrics()
  }

  const filteredInvoices = invoices.filter((inv) => {
    if (statusFilter !== 'all' && inv.status !== statusFilter) return false
    const q = searchQuery.trim().toLowerCase()
    if (!q) return true
    return (
      inv.invoice_number?.toLowerCase().includes(q) ||
      inv.patient_full_name?.toLowerCase().includes(q) ||
      inv.patient_id?.toLowerCase().includes(q)
    )
  })

  const grouped = groupInvoicesByMonth(filteredInvoices)

  const openDetail = (invoice: Invoice) => {
    setDetailInvoice(invoice)
    setDetailOpen(true)
  }

  const openPayment = (invoice: Invoice) => {
    setDetailOpen(false)
    setPaymentInvoice(invoice)
    setPaymentOpen(true)
  }

  const handleDownload = async (invoice: Invoice) => {
    try {
      const blob = await billingApi.downloadInvoice(invoice.id, 'es')
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Invoice_${invoice.invoice_number}.pdf`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.response?.data?.detail || 'Error al descargar factura',
        variant: 'destructive',
      })
    }
  }

  const handleDelete = async (invoice: Invoice) => {
    if (!window.confirm(`¿Eliminar la factura ${invoice.invoice_number}? Esta acción no se puede deshacer.`))
      return
    try {
      await billingApi.deleteInvoice(invoice.id)
      toast({ title: 'Factura eliminada', description: `Se eliminó la factura ${invoice.invoice_number}.` })
      reload()
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.response?.data?.detail || 'No se pudo eliminar la factura',
        variant: 'destructive',
      })
    }
  }

  const handleExport = () => {
    if (filteredInvoices.length === 0) {
      toast({ title: 'Sin datos', description: 'No hay facturas para exportar.' })
      return
    }
    const headers = ['Factura', 'Paciente', 'Fecha', 'Vencimiento', 'Total', 'Pagado', 'Saldo', 'Estado']
    const escape = (v: unknown) => {
      const s = String(v ?? '')
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const rows = filteredInvoices.map((inv) => [
      inv.invoice_number,
      inv.patient_full_name || inv.patient_id,
      inv.invoice_date,
      inv.due_date,
      Number(inv.total_amount).toFixed(2),
      Number(inv.amount_paid).toFixed(2),
      Number(inv.balance_due).toFixed(2),
      statusPillConfig[inv.status]?.label || inv.status,
    ])
    const csv = [headers, ...rows].map((r) => r.map(escape).join(',')).join('\n')
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `facturas_${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  const tabCount = (value: string) => {
    if (value === 'all') return total
    return metricsInvoices.filter((inv) => inv.status === value).length
  }

  return (
    <div className="animate-fade-in">
      {/* Top bar */}
      <ShiftBillingHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onNewInvoice={() => setCreateOpen(true)}
      />

      {/* KPI strip */}
      <ShiftBillingKPIs invoices={metricsInvoices} />

      {/* Status tabs + secondary controls */}
      <div className="space-y-3 mb-6">
        <div className="flex items-center justify-between gap-4">
          {/* Horizontal tabs */}
          <div className="flex items-center gap-0 overflow-x-auto border-b border-ml-outline-variant/10 flex-1">
            {STATUS_TABS.map((tab) => {
              const count = tabCount(tab.value)
              return (
                <button
                  key={tab.value}
                  onClick={() => setStatusFilter(tab.value)}
                  className={cn(
                    'flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold border-b-2 -mb-px transition-all whitespace-nowrap',
                    statusFilter === tab.value
                      ? 'border-ml-primary text-ml-primary'
                      : 'border-transparent text-ml-on-surface-variant/50 hover:text-ml-primary/70 hover:border-ml-primary/20',
                  )}
                >
                  {tab.label}
                  {count > 0 && (
                    <span
                      className={cn(
                        'text-[9px] font-bold px-1.5 py-0.5 rounded',
                        statusFilter === tab.value
                          ? 'bg-ml-primary/10 text-ml-primary'
                          : 'bg-ml-surface-container-high text-ml-on-surface-variant/50',
                      )}
                    >
                      {count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          {/* Date filter + export */}
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => setShowDateFilter((v) => !v)}
              className={cn(
                'flex items-center gap-1.5 text-xs font-bold transition-colors',
                dateFrom || dateTo || showDateFilter
                  ? 'text-ml-primary'
                  : 'text-ml-on-surface-variant/50 hover:text-ml-primary',
              )}
            >
              <Filter className="h-3.5 w-3.5" />
              {dateFrom || dateTo ? 'Rango activo' : 'Rango de fechas'}
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-1.5 text-xs font-bold text-ml-on-surface-variant/50 hover:text-ml-primary transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              Exportar
            </button>
          </div>
        </div>

        {/* Date range (collapsible) */}
        {showDateFilter && (
          <div className="flex items-end gap-3 px-4 py-3 bg-ml-surface-container-low rounded-xl">
            <div className="flex flex-col">
              <label className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/40 mb-1 px-1">
                Desde
              </label>
              <input
                type="date"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-ml-surface-container-lowest border-none rounded py-2 px-3 text-sm focus:ring-1 focus:ring-ml-primary/20"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/40 mb-1 px-1">
                Hasta
              </label>
              <input
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-ml-surface-container-lowest border-none rounded py-2 px-3 text-sm focus:ring-1 focus:ring-ml-primary/20"
              />
            </div>
            {(dateFrom || dateTo) && (
              <button
                onClick={() => {
                  setDateFrom('')
                  setDateTo('')
                }}
                className="text-xs font-bold text-ml-on-surface-variant/60 hover:text-ml-primary transition-colors pb-2"
              >
                Limpiar
              </button>
            )}
          </div>
        )}
      </div>

      {/* Invoice list */}
      <div className="space-y-8 mb-12">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ml-primary" />
          </div>
        ) : invoices.length === 0 ? (
          <BillingEmptyState type="no-invoices" onNewInvoice={() => setCreateOpen(true)} />
        ) : filteredInvoices.length === 0 ? (
          <BillingEmptyState
            type="no-results"
            onClearSearch={() => setSearchQuery('')}
            onClearFilter={() => setStatusFilter('all')}
          />
        ) : (
          grouped.map(({ label, invoices: groupItems }) => (
            <section key={label}>
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/40 px-1 mb-3">
                {label}
              </h3>
              <div className="space-y-2">
                {groupItems.map((invoice) => (
                  <InvoiceCard
                    key={invoice.id}
                    invoice={invoice}
                    onOpenDetail={openDetail}
                    onOpenPayment={openPayment}
                    onDownload={handleDownload}
                    onDelete={handleDelete}
                    onNavigatePatient={() => navigate(`/billing/patients/${invoice.patient_id}`)}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            </section>
          ))
        )}

        {/* Pagination */}
        {!loading && filteredInvoices.length > 0 && (
          <div className="flex items-center justify-between pt-2 px-1">
            <p className="text-[10px] font-bold text-ml-on-surface-variant/40 uppercase tracking-widest">
              {total === 0
                ? 'Sin facturas'
                : `Página ${page + 1} de ${totalPages} · ${total} facturas`}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-ml-outline-variant/20 text-ml-primary hover:bg-ml-surface-container-high transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-ml-primary text-white text-[10px] font-bold">
                {page + 1}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1 || loading}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-ml-outline-variant/20 text-ml-primary hover:bg-ml-surface-container-high transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Dialogs */}
      <CreateInvoiceDialog open={createOpen} onOpenChange={setCreateOpen} onSuccess={reload} />
      <InvoiceDetailDialog
        invoice={detailInvoice}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onRecordPayment={openPayment}
        onChanged={reload}
      />
      <RecordPaymentDialog
        invoice={paymentInvoice}
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        onSuccess={reload}
      />
    </div>
  )
}

// ─── InvoiceCard ──────────────────────────────────────────────────────────────

interface InvoiceCardProps {
  invoice: Invoice
  onOpenDetail: (inv: Invoice) => void
  onOpenPayment: (inv: Invoice) => void
  onDownload: (inv: Invoice) => void
  onDelete: (inv: Invoice) => void
  onNavigatePatient: () => void
  formatDate: (d: string) => string
}

function InvoiceCard({
  invoice,
  onOpenDetail,
  onOpenPayment,
  onDownload,
  onDelete,
  onNavigatePatient,
  formatDate,
}: InvoiceCardProps) {
  const status = statusPillConfig[invoice.status] || statusPillConfig.pending
  const canPay = Number(invoice.balance_due) > 0 && invoice.status !== 'cancelled'
  const method = invoice.payment_method ? paymentMethodConfig[invoice.payment_method] : null
  const initials = invoice.patient_full_name ? getInitials(invoice.patient_full_name) : 'NA'
  const firstService = invoice.line_items?.[0]?.service_description
  const extraCount = (invoice.line_items?.length ?? 0) - 1

  return (
    <div
      onClick={() => onOpenDetail(invoice)}
      className="group flex items-stretch bg-ml-surface-container-lowest hover:bg-ml-surface-container-low rounded-xl overflow-hidden cursor-pointer border border-ml-outline-variant/5 hover:border-ml-outline-variant/15 shadow-[0_1px_4px_rgba(3,36,26,0.04)] hover:shadow-[0_2px_10px_rgba(3,36,26,0.07)] transition-all"
    >
      {/* Left: ticket stub */}
      <div className="w-[92px] shrink-0 bg-ml-surface-container-high/50 border-r border-dashed border-ml-outline-variant/20 flex flex-col items-center justify-center py-4 px-3 text-center gap-1.5">
        <span className="text-[8px] font-bold uppercase tracking-widest text-ml-on-surface-variant/30">
          Factura
        </span>
        <span className="font-mono text-[11px] font-bold text-ml-primary leading-tight">
          #{invoice.invoice_number ? invoice.invoice_number.slice(-6) : '———'}
        </span>
        <div className="h-px w-5 bg-ml-outline-variant/20" />
        <span className="text-[9px] text-ml-on-surface-variant/45 leading-tight">
          {formatDate(invoice.invoice_date)}
        </span>
      </div>

      {/* Center: patient + service + status */}
      <div className="flex-1 py-4 px-5 min-w-0 flex flex-col justify-center gap-1.5">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-ml-secondary-container flex items-center justify-center text-[9px] font-bold text-ml-on-secondary-container shrink-0">
            {initials}
          </div>
          <span className="text-sm font-bold text-ml-primary truncate">
            {invoice.patient_full_name || `Paciente ${invoice.patient_id?.slice(0, 8)}`}
          </span>
        </div>

        {firstService && (
          <p className="text-xs text-ml-on-surface-variant/50 truncate pl-8">
            {firstService}
            {extraCount > 0 && (
              <span className="text-ml-on-surface-variant/35"> +{extraCount} más</span>
            )}
          </p>
        )}

        <div className="flex items-center gap-2 pl-8 mt-0.5 flex-wrap">
          <span
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold',
              status.pill,
            )}
          >
            <span className={cn('w-1 h-1 rounded-full shrink-0', status.dot)} />
            {status.label}
          </span>

          {method && (
            <span className="inline-flex items-center gap-1 text-[10px] text-ml-on-surface-variant/40 font-medium">
              {method.icon}
              {method.label}
            </span>
          )}

          {invoice.due_date && (
            <span className="text-[10px] text-ml-on-surface-variant/35">
              Vence {formatDate(invoice.due_date)}
            </span>
          )}
        </div>
      </div>

      {/* Right: amount + quick actions */}
      <div
        className="shrink-0 flex flex-col items-end justify-between py-4 px-4 gap-3 min-w-[120px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-right">
          <p className="text-base font-bold text-ml-primary">{formatCurrency(invoice.total_amount)}</p>
          {Number(invoice.balance_due) > 0 && Number(invoice.balance_due) !== Number(invoice.total_amount) && (
            <p className="text-[10px] text-amber-600 font-medium mt-0.5">
              Saldo {formatCurrency(invoice.balance_due)}
            </p>
          )}
        </div>

        <div className="flex items-center gap-0.5">
          <button
            title="Ver detalle"
            onClick={() => onOpenDetail(invoice)}
            className="p-1.5 rounded-md hover:bg-ml-surface-container-high text-ml-on-surface-variant/40 hover:text-ml-primary transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
          <button
            title="Descargar PDF"
            onClick={() => onDownload(invoice)}
            className="p-1.5 rounded-md hover:bg-ml-surface-container-high text-ml-on-surface-variant/40 hover:text-ml-primary transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
          >
            <Download className="h-3.5 w-3.5" />
          </button>
          {canPay && (
            <button
              title="Registrar pago"
              onClick={() => onOpenPayment(invoice)}
              className="p-1.5 rounded-md hover:bg-ml-surface-container-high text-ml-on-surface-variant/40 hover:text-ml-primary transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
            >
              <CreditCard className="h-3.5 w-3.5" />
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1.5 rounded-md hover:bg-ml-surface-container-high text-ml-on-surface-variant/40 hover:text-ml-primary transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 data-[state=open]:opacity-100">
                <MoreVertical className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onOpenDetail(invoice)}>
                <Eye className="h-4 w-4 mr-2" /> Ver detalle
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onNavigatePatient}>
                <User className="h-4 w-4 mr-2" /> Estado de cuenta
              </DropdownMenuItem>
              {canPay && (
                <DropdownMenuItem onClick={() => onOpenPayment(invoice)}>
                  <CreditCard className="h-4 w-4 mr-2" /> Registrar pago
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => onDownload(invoice)}>
                <Download className="h-4 w-4 mr-2" /> Descargar PDF
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-ml-error focus:text-ml-error"
                onClick={() => onDelete(invoice)}
              >
                <Trash2 className="h-4 w-4 mr-2" /> Eliminar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  )
}

// ─── Empty state ──────────────────────────────────────────────────────────────

interface EmptyStateProps {
  type: 'no-invoices' | 'no-results'
  onNewInvoice?: () => void
  onClearSearch?: () => void
  onClearFilter?: () => void
}

function BillingEmptyState({ type, onNewInvoice, onClearSearch, onClearFilter }: EmptyStateProps) {
  if (type === 'no-invoices') {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center rounded-2xl border border-dashed border-ml-outline-variant/20">
        <div className="w-12 h-12 rounded bg-ml-surface-container-high flex items-center justify-center mb-4">
          <Receipt className="h-5 w-5 text-ml-on-surface-variant/40" />
        </div>
        <h3 className="text-base font-bold text-ml-primary mb-1">Sin facturas aún</h3>
        <p className="text-sm text-ml-on-surface-variant/60 max-w-xs mb-5">
          Crea tu primera factura o hazlo desde el espacio de trabajo de un paciente.
        </p>
        {onNewInvoice && (
          <button
            onClick={onNewInvoice}
            className="flex items-center gap-1.5 bg-ml-primary text-white rounded px-5 py-2 text-xs font-bold hover:opacity-90 transition-opacity"
          >
            <Plus className="h-3.5 w-3.5" /> Nueva Factura
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center rounded-2xl border border-dashed border-ml-outline-variant/20">
      <div className="w-12 h-12 rounded bg-ml-surface-container-high flex items-center justify-center mb-4">
        <Filter className="h-5 w-5 text-ml-on-surface-variant/40" />
      </div>
      <h3 className="text-base font-bold text-ml-primary mb-1">Sin resultados</h3>
      <p className="text-sm text-ml-on-surface-variant/60 max-w-xs mb-5">
        Ninguna factura coincide con el filtro o búsqueda actual.
      </p>
      <div className="flex items-center gap-2">
        {onClearSearch && (
          <button onClick={onClearSearch} className="text-xs font-bold text-ml-primary hover:underline">
            Limpiar búsqueda
          </button>
        )}
        {onClearSearch && onClearFilter && (
          <span className="text-ml-on-surface-variant/30 text-xs">·</span>
        )}
        {onClearFilter && (
          <button onClick={onClearFilter} className="text-xs font-bold text-ml-primary hover:underline">
            Ver todas
          </button>
        )}
      </div>
    </div>
  )
}
